<template>
  <!-- Oct 2026 - sites manager SHARED with suppliers: the portal Company Sites page, supplier onboarding tasks, etc.
       LIST of the company's sites on the left, the selected site (CompanySiteDetail) on the right
       everything goes through the CompanySite workflow (vendors/flow_defs/company_site) -- new sites through its start nodes,
       changes through its transitions -- so the per-tenant rules apply (suppliers' changes to active sites may wait for approval) -->
  <div class="shared-company-sites-manager">
    <b-alert
      :show="!!errorHandlingMessage"
      variant="danger"
      dismissible
    >{{ errorHandlingMessage }}</b-alert>

    <div class="d-flex align-items-end justify-content-between gap-3 mb-2">
      <div
        v-if="sites && sites.length"
        class="special-text text-muted"
      >{{ $t('sharedCompanySitesManager.intro') }}</div>
      <b-button
        v-if="!readOnly && startNodeKey"
        variant="sharp-success"
        class="flex-shrink-0 ml-auto"
        @click="openAddSite"
      >
        <b-icon
          icon="plus"
          class="mr-1"
        ></b-icon>
        {{ $t('sharedCompanySitesManager.addSite') }}
      </b-button>
    </div>

    <b-overlay :show="loadingSites">
      <p
        v-if="!loadingSites && !sites.length"
        class="text-muted my-4"
      >{{ $t('sharedCompanySitesManager.noSites') }}</p>

      <div
        v-else
        class="d-flex flex-column flex-md-row gap-3"
      >
        <!-- LEFT: the sites -->
        <div
          class="shared-company-sites-manager__list bg-white border rounded flex-shrink-0"
          :class="noShadow ? '' : 'shadow-sm'"
        >
          <div
            v-for="site in sites"
            :key="site.id"
            class="shared-company-sites-manager__item"
            :class="{ active: site.id === selectedSiteId, 'has-you-badge': hasReviewForMe(site) }"
            role="button"
            tabindex="0"
            @click="selectSite(site.id)"
            @keydown.enter="selectSite(site.id)"
          >
            <div
              v-if="hasReviewForMe(site)"
              class="is-assigned-tu-badge special-task-assignee-badge shadow-sm"
              :title="$t('sharedCompanySitesManager.reviewWaiting')"
            >{{ $t('sharedCompanySitesManager.you') }}</div>
            <div class="d-flex align-items-center gap-2">
              <span class="font-weight-600">{{ site.display_name }}</span>
              <b-icon
                v-if="site.is_primary"
                icon="star-fill"
                variant="warning"
                :title="$t('sharedCompanySitesManager.primarySite')"
              ></b-icon>
              <b-icon
                v-if="(site.problems || []).length"
                icon="exclamation-triangle-fill"
                variant="warning"
                class="ml-auto"
                :title="$t('sharedCompanySitesManager.somethingMissing')"
              ></b-icon>
            </div>
            <div class="d-flex flex-wrap align-items-center gap-2 mt-1">
              <b-badge
                :variant="stateParams(site).variant"
                class="text-uppercase"
              >{{ stateParams(site).label }}</b-badge>
              <b-badge
                v-if="site.has_pending_change"
                variant="soft-info"
                class="text-uppercase"
              >{{ $t('sharedCompanySitesManager.changesPending') }}</b-badge>
            </div>
            <div
              v-if="site.address && site.address.formatted_city_state_country"
              class="font-size-80 text-muted mt-1"
            >{{ site.address.formatted_city_state_country }}</div>
          </div>
        </div>

        <!-- RIGHT: the selected site -- :key gives every site a fresh CompanySiteDetail (no edit state carried between sites) -->
        <div class="flex-fill min-width-0">
          <company-site-detail
            v-if="selectedSiteId"
            :key="selectedSiteId"
            :site-id="selectedSiteId"
            :read-only="readOnly"
            @saved="onSiteChanged"
            @deleted="onSiteRemoved"
          ></company-site-detail>
        </div>
      </div>
    </b-overlay>

    <!-- ADD A SITE (CompanySite start node) -- just the name + site types, the rest is filled in on the site -->
    <b-modal
      v-model="addModalShowing"
      :title="$t('sharedCompanySitesManager.addSite')"
      size="md"
      centered
      hide-footer
      no-close-on-backdrop
    >
      <b-overlay :show="loadingStartForm">
        <p
          v-if="startForm && startForm.instructions"
          class="mb-3"
        >{{ startForm.instructions }}</p>
        <!-- keep the form in the DOM while saving - backend errors are shown through it -->
        <FormulateForm
          v-if="startForm"
          ref="startForm"
          v-model="startFormData"
          name="sharedSitesStartForm"
          :schema="startForm.items || []"
          @submit="submitStartForm"
          #default="{ isLoading, hasErrors }"
        >
          <div class="mt-3 d-flex justify-content-end gap-3">
            <b-button
              variant="gray-dark"
              :disabled="savingStartForm"
              @click="addModalShowing = false"
            >{{ $t('sharedCompanySitesManager.cancel') }}</b-button>
            <b-button
              type="submit"
              variant="primary"
              :disabled="hasErrors || isLoading || savingStartForm"
            >
              <b-spinner
                v-if="savingStartForm"
                small
                class="mr-1"
              ></b-spinner>
              {{ $t('sharedCompanySitesManager.saveSite') }}
            </b-button>
          </div>
        </FormulateForm>
      </b-overlay>
    </b-modal>
  </div>
</template>

<script>
  import isObject from 'lodash/isObject';
  import { suppliosApi, getFlowObjectApi } from '@/js/api-service';
  import { errorHandling } from '@/js/custom-vue-mixins';
  import CompanySiteDetail from '@/components/vendors/components/CompanySiteDetail.vue';

  // the start nodes we can use to add a site (vendors/flow_defs/company_site/nodes.py) -- the backend only returns the ones the user may use
  const START_NODE_KEYS = ['start_site_supplier', 'start_site_internal'];

  export default {
    name: 'SharedCompanySitesManager',
    components: {
      CompanySiteDetail,
    },
    mixins: [errorHandling],
    i18n: {
      // English only for now -- other languages fall back to English
      messages: {
        en: {
          sharedCompanySitesManager: {
            intro: 'NOTE: Adding new sites or updating existing ones may require review.',
            addSite: 'Add Site',
            saveSite: 'Add Site',
            cancel: 'Cancel',
            noSites: 'No sites yet - add your first site.',
            primarySite: 'Primary site',
            somethingMissing: 'Something is still missing for this site',
            changesPending: 'Changes pending',
            reviewWaiting: 'Waiting for your review',
            you: 'You',
            stateNew: 'New',
            stateActive: 'Active',
            stateInactive: 'Inactive',
            stateRemoved: 'Removed',
            errorLoading: 'Error while loading sites',
            errorStartForm: 'Error while loading the site form',
          },
        },
      },
    },
    props: {
      companyId: {
        type: String,
        required: true,
      },
      readOnly: {
        // just show the sites -- no add / edits / actions
        type: Boolean,
        default: false,
      },
      noShadow: {
        type: Boolean,
        default: false,
      },
    },
    data() {
      return {
        loadingSites: false,
        sites: [],
        selectedSiteId: null,
        // add site
        startNodeKey: null,
        addModalShowing: false,
        loadingStartForm: false,
        savingStartForm: false,
        startForm: null,
        startFormData: {},
      };
    },
    computed: {
      siteFlowApi() {
        return getFlowObjectApi('CompanySite');
      },
    },
    async mounted() {
      await Promise.all([this.reloadSites(), this.loadStartNodeKey()]);
      this.selectDefaultSite();
    },
    methods: {
      // PUBLIC - parents can call this to refresh, like $refs.sitesManager.reloadSites()
      async reloadSites() {
        this.loadingSites = true;
        try {
          const response = await suppliosApi.companySite.list({ params: { company: this.companyId } });
          this.sites = Array.isArray(response.data) ? response.data : [];
        } catch (error) {
          this.apiErrorHandler(error, this.$t('sharedCompanySitesManager.errorLoading'), true);
          this.sites = [];
        } finally {
          this.loadingSites = false;
        }
        this.$emit('sites-loaded', this.sites);
      },
      selectDefaultSite() {
        if (this.selectedSiteId && this.sites.some((site) => site.id === this.selectedSiteId)) return;
        // start where something is missing, otherwise the primary site
        const site = this.sites.find((s) => (s.problems || []).length) || this.sites.find((s) => s.is_primary) || this.sites[0];
        this.selectedSiteId = site ? site.id : null;
      },
      selectSite(siteId) {
        this.selectedSiteId = siteId;
      },
      hasReviewForMe(site) {
        return !!(site.my_review_tasks && site.my_review_tasks.length);
      },
      stateParams(site) {
        const params = {
          new: { label: this.$t('sharedCompanySitesManager.stateNew'), variant: 'soft-info' },
          active: { label: this.$t('sharedCompanySitesManager.stateActive'), variant: 'soft-success' },
          inactive: { label: this.$t('sharedCompanySitesManager.stateInactive'), variant: 'soft-secondary' },
          removed: { label: this.$t('sharedCompanySitesManager.stateRemoved'), variant: 'soft-danger' },
        };
        return params[site.sm_state] || { label: site.sm_state || '', variant: 'soft-secondary' };
      },
      notifyChanged() {
        this.$emit('sites-changed');
      },
      async onSiteChanged() {
        await this.reloadSites();
        this.notifyChanged();
      },
      async onSiteRemoved() {
        this.selectedSiteId = null;
        await this.reloadSites();
        this.selectDefaultSite();
        this.notifyChanged();
      },
      // ADD A SITE
      async loadStartNodeKey() {
        if (this.readOnly) return;
        try {
          const response = await this.siteFlowApi.get_start_nodes();
          const keys = (response.data || []).map((node) => node.key);
          this.startNodeKey = START_NODE_KEYS.find((key) => keys.includes(key)) || null;
        } catch (error) {
          console.error('Error loading the CompanySite start nodes', error);
          this.startNodeKey = null;
        }
      },
      async openAddSite() {
        this.startForm = null;
        this.startFormData = { company: this.companyId }; // the internal start form has a company picker -- this manager is for ONE company
        this.addModalShowing = true;
        this.loadingStartForm = true;
        try {
          const response = await this.siteFlowApi.get_start_node_form({ params: { node_key: this.startNodeKey } });
          this.startForm = response.data?.form || null;
        } catch (error) {
          this.apiErrorHandler(error, this.$t('sharedCompanySitesManager.errorStartForm'), true);
          this.addModalShowing = false;
        } finally {
          this.loadingStartForm = false;
        }
      },
      async submitStartForm(submitData) {
        this.savingStartForm = true;
        this.$formulate.resetValidation('sharedSitesStartForm');
        const existingIds = this.sites.map((site) => site.id);
        try {
          await this.siteFlowApi.submit_start_node_form({ node_key: this.startNodeKey, with_save: true, form_data: { ...submitData } });
          this.addModalShowing = false;
          await this.reloadSites();
          // select the new site, so the rest can be filled in right away
          const newSite = this.sites.find((site) => !existingIds.includes(site.id));
          if (newSite) this.selectedSiteId = newSite.id;
          this.notifyChanged();
        } catch (error) {
          // field errors come back keyed by field name -- anything else is shown as a form error (same as FlowObjectStarter)
          if (error.response?.data && isObject(error.response.data)) {
            const fieldMap = this.$refs.startForm?.registry?.registry || new Map();
            const inputErrors = {};
            const formErrors = [];
            Object.entries(error.response.data).forEach(([errKey, errMsgs]) => {
              const msgs = Array.isArray(errMsgs) ? errMsgs : [String(errMsgs)];
              if (fieldMap.has(errKey)) {
                inputErrors[errKey] = msgs;
              } else {
                formErrors.push(...msgs);
              }
            });
            this.$formulate.handle({ inputErrors, formErrors }, 'sharedSitesStartForm');
          } else {
            this.apiErrorHandler(error);
          }
        } finally {
          this.savingStartForm = false;
        }
      },
    },
  };
</script>

<style lang="scss" scoped>
  .shared-company-sites-manager__list {
    width: 100%;
    max-height: 70vh;
    overflow-y: auto;

    @media (min-width: 768px) {
      width: 280px;
    }
  }

  .shared-company-sites-manager__item {
    position: relative; // for the 'You' badge
    cursor: pointer;
    padding: 0.5rem 1rem;
    border-left: 5px solid transparent;
    border-bottom: 1px solid var(--gray-200);

    &:hover {
      background-color: var(--gray-50);
    }

    &.active {
      background-color: var(--primary-50);
      border-left-color: var(--primary-500);
    }

    &.has-you-badge {
      padding-top: 1.25rem; // room for the 'You' badge above the name
    }
  }

  .min-width-0 {
    min-width: 0;
  }

  // 'You' badge -- same look as the task list badge in FlowObjectDetail (.special-task-assignee-badge / .is-assigned-tu-badge)
  .special-task-assignee-badge {
    position: absolute;
    top: 4px;
    left: -2px;
    padding: 2px 4px 1px 4px;
    font-weight: 700;
    font-size: 0.6rem;
    text-transform: uppercase;
    border-width: 1px 1px 1px 0;
    border-style: solid;
    border-color: transparent;
    border-top-right-radius: 6px;
    border-bottom-right-radius: 6px;
  }

  .is-assigned-tu-badge {
    color: var(--danger-600);
    background-color: var(--danger-100);
  }
</style>
